import { Box, Button, Grid } from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Flex from "@/components/Flex";
import { ScrollArea } from "@/components/ScrollBar";
import Text from "@/components/Text";
import { TherapistSlot } from "@/queries/group-visit/use-therapist-slots";
import { formatTime } from "@/utils/date-time-utility";
import { GROUP_SLOT_LABEL, SLOT_PICKER_SX } from "../group-visit-constant";
import {
  TherapistSlotSearch,
  useTherapistSlotsForDay,
} from "../hooks/use-therapist-slots";

export type GroupSlotPickerProps = {
  search: TherapistSlotSearch;
  selectedDate: Dayjs | null;
  selectedStartTime: string | null;
  onDateChange: (date: Dayjs | null) => void;
  onSlotSelect: (slot: TherapistSlot) => void;
};

const formatSlotRange = (slot: TherapistSlot) =>
  `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`;

/** Calendar plus the therapists' open slots for the chosen day. */
const GroupSlotPicker = ({
  search,
  selectedDate,
  selectedStartTime,
  onDateChange,
  onSlotSelect,
}: GroupSlotPickerProps) => {
  const { slots, isLoading } = useTherapistSlotsForDay(search, selectedDate);

  const emptyMessage = !selectedDate
    ? GROUP_SLOT_LABEL.selectDate
    : isLoading
      ? GROUP_SLOT_LABEL.loading
      : GROUP_SLOT_LABEL.noSlots;

  return (
    <Grid
      container
      sx={{
        width: SLOT_PICKER_SX.width,
      }}
    >
      <Grid size={5}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateCalendar
            views={["year", "month", "day"]}
            minDate={dayjs()}
            value={selectedDate}
            onChange={(date) => onDateChange(date)}
          />
        </LocalizationProvider>
      </Grid>

      <Grid
        sx={{
          mt: 2,
        }}
        size={7}
      >
        {!!selectedDate && slots.length > 0 ? (
          <ScrollArea height={SLOT_PICKER_SX.listHeight} width="100%">
            <Flex flexWrap="wrap" gap={1} width="100%">
              {slots.map((slot) => (
                <Box
                  key={slot.startTime}
                  sx={{
                    width: SLOT_PICKER_SX.slotWidth,
                  }}
                >
                  <Button
                    fullWidth
                    variant={
                      slot.startTime === selectedStartTime
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => onSlotSelect(slot)}
                  >
                    <Text>{formatSlotRange(slot)}</Text>
                  </Button>
                </Box>
              ))}
            </Flex>
          </ScrollArea>
        ) : (
          <Flex
            height="100%"
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <Text>{emptyMessage}</Text>
          </Flex>
        )}
      </Grid>
    </Grid>
  );
};

export default GroupSlotPicker;
